// src/components/Account.js

import React, { useEffect, useState, useRef } from 'react';
import './account.css';
import { auth, db, functions, storage } from '../../firebase'; // Ensure 'storage' is exported from your firebase config
import { signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot, updateDoc, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, Link } from 'react-router-dom'; // If using react-router
import UserPrompts from './UserPrompts'; // Import the UserPrompts component
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faEdit, faTrash, faSave, faTimes, faEye, faEyeSlash, faShieldAlt } from '@fortawesome/free-solid-svg-icons'; // Import icons
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Import storage functions
import { getStorage } from "firebase/storage";
import { useAdmin } from '../../contexts/AdminContext'; // Import admin context

// STRIPE
import { loadStripe } from '@stripe/stripe-js';



// Initialize Stripe outside the component to ensure it's loaded only once
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY); // Ensure REACT_APP_STRIPE_PUBLIC_KEY is set in your .env

const Account = () => {
  const { isAdmin, isLoading: adminLoading } = useAdmin();
  const [userProfile, setUserProfile] = useState({
    profileImage: '',
    userEmail: '',
  });
  const [personalBest, setPersonalBest] = useState(null);
  const [quote, setQuote] = useState(null);
  const [prompts, setPrompts] = useState([]); // State for prompts array
  const [AIResponses, setAIResponses] = useState([]); // State for AI responses array
  const [loading, setLoading] = useState(true); // For loading state
  const [error, setError] = useState(null); // For error handling
  const [uploading, setUploading] = useState(false); // For upload loading state
  const [uploadError, setUploadError] = useState(null); // For upload error
  const navigate = useNavigate(); // If using react-router
  const fileInputRef = useRef(null); // Ref for hidden file input

  // Purchase State
  const [purchaseButtonText, setPurchaseButtonText] = useState("Purchase");

  // Fixed price
  const purchasePrice = "5.00"; // Fixed price of $5.00

  const [username, setUsername] = useState('');
  const [usernameEdit, setUsernameEdit] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const [emailEdit, setEmailEdit] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Refs for keyboard navigation
  const usernameInputRef = useRef(null);
  const usernameSaveButtonRef = useRef(null);
  const emailInputRef = useRef(null);
  const emailSaveButtonRef = useRef(null);

  const initiatePurchase = async () => {
    setPurchaseButtonText("Processing...");
    try {
      const functionsInstance = functions;
      const createSession = httpsCallable(functionsInstance, 'startPaymentSession'); // Renamed cloud function

      // Retrieve gclid from localStorage if needed
      const gclid = localStorage.getItem('gclid') || '';

      // Prepare payload
      const payload = {
        plan: 'Messagly', // Fixed plan name
        gclid: gclid,
      };

      // Call the Firebase function with the prepared payload
      const response = await createSession(payload);
      
      const { sessionId } = response.data;
      const stripe = await stripePromise;

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        // Handle Stripe redirection errors
        setPurchaseButtonText("Retry");
      } else {
        // Reset button text if redirection is successful
        setPurchaseButtonText("Purchase");
      }
    } catch (err) {
      // Handle errors from the backend function
      console.error('Purchase Error:', err);
      setPurchaseButtonText("Retry");
    }
  };

  const handlePurchaseClick = () => {
    initiatePurchase();
  };


  useEffect(() => {
    const fetchUserProfile = async () => {
      const user = auth.currentUser;
      console.log("Current user:", user); // Log the current user object for debugging

      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid); // Path to 'users/{userId}'
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            setUserProfile(userDoc.data().userProfile);
            const data = userDoc.data().userProfile || {};
            setUsername(data.username || '');
            setUsernameEdit(data.username || '');
            setEmailEdit(data.userEmail || user.email || '');
          } else {
            console.log('No such document!');
            setError('User profile not found.');
          }
          
          // Fetch personal best times
          const personalBestDocRef = doc(db, 'userPersonalBests', user.uid);
          const personalBestDoc = await getDoc(personalBestDocRef);
          
          if (personalBestDoc.exists()) {
            setPersonalBest(personalBestDoc.data());
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          setError('Failed to fetch user profile.');
        }
      }
    };

    const selectRandomQuote = () => {
      const allQuotes = [
        "The greatest glory in living lies not in never falling, but in rising every time we fall. — Nelson Mandela",
        "The way to get started is to quit talking and begin doing. — Walt Disney",
        "Your time is limited, so don't waste it living someone else's life. — Steve Jobs",
        // Add more quotes as desired
      ];

      const randomIndex = Math.floor(Math.random() * allQuotes.length);
      setQuote(allQuotes[randomIndex]);
    };

    const fetchData = async () => {
      await fetchUserProfile();
      selectRandomQuote();
      setLoading(false);
    };

    // Set up auth state listener
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchData();

        // Set up Firestore listener for prompts
        const userDocRef = doc(db, 'users', user.uid);
        const unsubscribePrompts = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setPrompts(data.prompts || []);
            setAIResponses(data.AIResponses || []);
          } else {
            console.log('No such document for prompts!');
            setPrompts([]);
            setAIResponses([]);
          }
        }, (err) => {
          console.error('Error fetching prompts and AI responses:', err);
          setError('Failed to fetch prompts and AI responses.');
        });

        // Cleanup Firestore listener on unmount or user change
        return () => {
          unsubscribePrompts();
        };
      } else {
        // Only redirect if we're sure there's no user
        if (!loading) {
          navigate('/inloggen');
        }
      }
    });

    return () => unsubscribeAuth();
  }, [navigate, loading]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login'); // Redirect to login after logout
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to log out.');
    }
  };

  const handleUserPromptSubmit = async (prompt) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        // Add the prompt to 'prompts' array
        await updateDoc(userDocRef, {
          prompts: arrayUnion(prompt),
        });

        // Call the Firebase Cloud Function to generate AI response
        const generateAI = httpsCallable(functions, 'generate_completion');
        const result = await generateAI({ userPrompt: prompt });

        if (result.data && result.data.reply) {
          const aiReply = result.data.reply;
          // Add the AI response to 'AIResponses' array
          await updateDoc(userDocRef, {
            AIResponses: arrayUnion(aiReply),
          });
        } else {
          console.error('Invalid response from Cloud Function:', result.data);
          setError('Failed to get a valid response from AI.');
        }
      } catch (err) {
        console.error('Error handling user prompt submission:', err);
        setError('Failed to submit prompt or retrieve AI response.');
      }
    } else {
      setError('User not authenticated.');
    }
  };

  // Handler for file input change
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    const user = auth.currentUser;
    if (!user) {
      setUploadError('User not authenticated.');
      setUploading(false);
      return;
    }

    // Check file size (e.g., 2 GB limit)
    if (file.size > 2 * 1024 * 1024 * 1024) {
      setUploadError('File size exceeds 2 GB.');
      setUploading(false);
      return;
    }

    try {
      // Create a storage reference
      const storageRefInstance = ref(storage, `profile_pictures/${user.uid}/${file.name}`);
      
      // Upload the file
      await uploadBytes(storageRefInstance, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRefInstance);

      // Update the user's profile in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'userProfile.profileImage': downloadURL,
      });

      // Update local state
      setUserProfile((prevProfile) => ({
        ...prevProfile,
        profileImage: downloadURL,
      }));

    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadError('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  // Function to trigger the hidden file input
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUsernameSave = async () => {
    if (usernameLoading || usernameEdit === username) return;
    
    setUsernameLoading(true);
    setUsernameError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'userProfile.username': usernameEdit
      });
      setUsername(usernameEdit);
      
      // Focus back on the input after successful save
      if (usernameInputRef.current) {
        usernameInputRef.current.focus();
      }
    } catch (err) {
      setUsernameError('Failed to update username.');
    } finally {
      setUsernameLoading(false);
    }
  };
  
  // Handle key press in username input
  const handleUsernameKeyDown = (e) => {
    if (e.key === 'Enter' && !usernameLoading && usernameEdit !== username) {
      e.preventDefault();
      handleUsernameSave();
    }
  };

  const handleEmailSave = async () => {
    if (emailLoading || emailEdit === userProfile.userEmail) return;
    
    setEmailLoading(true);
    setEmailError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      // Update email in Firebase Auth
      await user.updateEmail(emailEdit);
      // Update email in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'userProfile.userEmail': emailEdit
      });
      setUserProfile((prev) => ({ ...prev, userEmail: emailEdit }));
      
      // Focus back on the input after successful save
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      }
    } catch (err) {
      setEmailError('Failed to update email. (Tip: You may need to re-authenticate)');
    } finally {
      setEmailLoading(false);
    }
  };
  
  // Handle key press in email input
  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' && !emailLoading && emailEdit !== userProfile.userEmail) {
      e.preventDefault();
      handleEmailSave();
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <p className="loading">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-header">
        {username ? `Welkom ${username}!` : 'Welkom!'}
      </h1>
      
      <div className="profile-section">
        {/* Profile image/placeholder on the left */}
        <div className="profile-image-wrapper">
          {userProfile.profileImage ? (
            <img
              src={userProfile.profileImage}
              alt="Profile"
              className="profile-image profile-image-clickable"
              onClick={triggerFileInput}
              title="Klik om je profielfoto te wijzigen"
              style={{ cursor: 'pointer' }}
            />
          ) : (
            <div
              className="profile-placeholder profile-image-clickable"
              onClick={triggerFileInput}
              title="Klik om je profielfoto te uploaden"
              style={{ cursor: 'pointer' }}
            >
              No Image
            </div>
          )}
          {uploading && <p className="uploading">Uploading...</p>}
          {uploadError && <p className="upload-error">{uploadError}</p>}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
        {/* Fields on the right */}
        <div className="profile-fields">
          <div className="username-section">
            <label htmlFor="username-input">Username:</label>
            <input
              id="username-input"
              type="text"
              value={usernameEdit}
              onChange={e => setUsernameEdit(e.target.value)}
              onKeyDown={handleUsernameKeyDown}
              disabled={usernameLoading}
              className="username-input"
              ref={usernameInputRef}
              aria-label="Edit your username"
            />
            <button 
              onClick={handleUsernameSave} 
              disabled={usernameLoading || usernameEdit === username}
              ref={usernameSaveButtonRef}
              aria-label="Save username changes"
            >
              Save
            </button>
            {usernameError && <div className="error-message" role="alert">{usernameError}</div>}
          </div>
          <div className="username-section">
            <label htmlFor="email-input">E-mail:</label>
            <span className="user-email" id="email-input">{userProfile.userEmail || emailEdit}</span>
          </div>
        </div>
      </div>

      {/* HSBakRad Templates Section */}
      <div className="hsbakrad-templates-section">
        <h2 className="section-title">Mijn HSBakRad Templates</h2>
        <div className="templates-container">
          <HSBakRadTemplates />
        </div>
      </div>
      
      {/* Personal Best Times Section - Compact Version */}
      <div className="personal-best-section-compact">
        <h3 className="compact-section-header">Je snelste baktijden</h3>
        
        <div className="compact-personal-best-content">
          {personalBest ? (
            <>
              <div className="compact-personal-best-times">
                <div className="compact-time-item">
                  <span className="compact-time-label">1BAK Tijd:</span>
                  <span className="compact-time-value">{personalBest.time1bak || '--:--'}</span>
                </div>
                <div className="compact-time-item">
                  <span className="compact-time-label">3BAK Tijd:</span>
                  <span className="compact-time-value">{personalBest.time3bak || '--:--'}</span>
                </div>
              </div>
              <Link to="/hsbakbord" className="compact-personal-best-link">
                Tijden updaten
              </Link>
              </>
          ) : (
            <>
              <p className="compact-no-times">You haven't set any personal best times yet</p>
              <Link to="/hsbakbord" className="compact-personal-best-link">
                Set Your Times Now
              </Link>
            </>
          )}
        </div>
      </div>

      {isAdmin && !adminLoading && (
        <Link to="/admin" className="admin-link">
          <FontAwesomeIcon icon={faShieldAlt} /> Admin Dashboard
        </Link>
      )}
      
      <button className="logout-button" onClick={handleLogout}>
        Uitloggen
      </button>
    </div>
  );
};

// HSBakRad Templates Component
const HSBakRadTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editName, setEditName] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [noTemplates, setNoTemplates] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  
  // Segment editing states
  const [editTemplateIndex, setEditTemplateIndex] = useState(null);
  const [editSegmentIndex, setEditSegmentIndex] = useState(null);
  const [editSegmentName, setEditSegmentName] = useState("");

  useEffect(() => {
    // Load templates from localStorage
    const loadTemplates = () => {
      const stored = localStorage.getItem("hsbakrad_templates");
      if (stored) {
        const parsedTemplates = JSON.parse(stored);
        setTemplates(parsedTemplates);
        setNoTemplates(parsedTemplates.length === 0);
      } else {
        setNoTemplates(true);
      }
    };

    loadTemplates();
  }, []);

  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditName(templates[index].name);
  };

  const handleSaveEdit = (index) => {
    if (!editName.trim()) return;
    
    const updatedTemplates = [...templates];
    updatedTemplates[index].name = editName.trim();
    
    setTemplates(updatedTemplates);
    localStorage.setItem("hsbakrad_templates", JSON.stringify(updatedTemplates));
    
    setEditIndex(null);
    setEditName("");
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditName("");
  };
  
  const toggleTemplateExpand = (index) => {
    if (expandedTemplate === index) {
      setExpandedTemplate(null);
      // Reset any segment editing state when collapsing
      handleCancelSegmentEdit();
    } else {
      setExpandedTemplate(index);
    }
  };
  
  // Segment editing handlers
  const handleEditSegmentClick = (templateIndex, segmentIndex, segmentName) => {
    setEditTemplateIndex(templateIndex);
    setEditSegmentIndex(segmentIndex);
    setEditSegmentName(segmentName);
  };
  
  const handleCancelSegmentEdit = () => {
    setEditTemplateIndex(null);
    setEditSegmentIndex(null);
    setEditSegmentName("");
  };
  
  const handleSaveSegmentEdit = (templateIndex, segmentIndex) => {
    if (!editSegmentName.trim()) return;
    
    const updatedTemplates = [...templates];
    updatedTemplates[templateIndex].segments[segmentIndex].option = editSegmentName.trim();
    
    setTemplates(updatedTemplates);
    localStorage.setItem("hsbakrad_templates", JSON.stringify(updatedTemplates));
    
    handleCancelSegmentEdit();
  };
  
  const handleDeleteSegmentClick = (templateIndex, segmentIndex) => {
    const segmentName = templates[templateIndex].segments[segmentIndex].option;
    setConfirmationMessage(`Weet je zeker dat je segment "${segmentName}" wilt verwijderen?`);
    setConfirmationAction(() => () => {
      const updatedTemplates = [...templates];
      updatedTemplates[templateIndex].segments = updatedTemplates[templateIndex].segments.filter((_, i) => i !== segmentIndex);
      
      setTemplates(updatedTemplates);
      localStorage.setItem("hsbakrad_templates", JSON.stringify(updatedTemplates));
    });
    setShowConfirmation(true);
  };
  
  const handleAddSegment = (templateIndex) => {
    // Define a pool of colors to cycle through
    const colorPool = ["#FF3E3E", "#00A1FF", "#FFD700", "#9C27B0", "#4CAF50", "#0071e3", "#2A4759"];
    const segments = templates[templateIndex].segments;
    const nextColorIndex = segments.length % colorPool.length;
    const backgroundColor = colorPool[nextColorIndex];
    const textColor = backgroundColor === "#FFD700" ? "#000000" : "#ffffff";
    
    const newSegment = {
      option: "Nieuw segment",
      style: {
        backgroundColor,
        textColor
      }
    };
    
    const updatedTemplates = [...templates];
    updatedTemplates[templateIndex].segments.push(newSegment);
    
    setTemplates(updatedTemplates);
    localStorage.setItem("hsbakrad_templates", JSON.stringify(updatedTemplates));
    
    // Automatically start editing the new segment
    const newSegmentIndex = updatedTemplates[templateIndex].segments.length - 1;
    handleEditSegmentClick(templateIndex, newSegmentIndex, "Nieuw segment");
  };

  const handleDeleteClick = (index) => {
    setConfirmationMessage(`Weet je zeker dat je template "${templates[index].name}" wilt verwijderen?`);
    setConfirmationAction(() => () => {
      const updatedTemplates = templates.filter((_, i) => i !== index);
      setTemplates(updatedTemplates);
      localStorage.setItem("hsbakrad_templates", JSON.stringify(updatedTemplates));
      setNoTemplates(updatedTemplates.length === 0);
    });
    setShowConfirmation(true);
  };

  const handleConfirmYes = () => {
    if (confirmationAction) {
      confirmationAction();
    }
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const handleConfirmNo = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  return (
    <div className="hsbakrad-templates-component">
      {/* Confirmation dialog */}
      <div className={`template-confirmation-overlay ${showConfirmation ? 'visible' : ''}`}>
        <div className="template-confirmation-dialog">
          <p className="template-confirmation-message">{confirmationMessage}</p>
          <div className="template-confirmation-buttons">
            <button className="template-confirm-yes-btn" onClick={handleConfirmYes}>Ja</button>
            <button className="template-confirm-no-btn" onClick={handleConfirmNo}>Nee</button>
          </div>
        </div>
      </div>

      {noTemplates ? (
        <div className="no-templates-message">
          <p>Je hebt nog geen HSBakRad templates opgeslagen.</p>
          <p>Ga naar de <a href="/hsbakrad" className="hsbakrad-link">HSBakRad pagina</a> om templates aan te maken.</p>
        </div>
      ) : (
        <div className="templates-list">
          {templates.map((template, index) => (
            <div key={index} className={`template-item ${expandedTemplate === index ? 'expanded' : ''}`}>
              <div className="template-header">
                {editIndex === index ? (
                  <div className="template-edit-form">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="template-edit-input"
                      autoFocus
                    />
                    <div className="template-edit-buttons">
                      <button 
                        className="template-save-btn" 
                        onClick={() => handleSaveEdit(index)}
                        title="Opslaan"
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                      <button 
                        className="template-cancel-btn" 
                        onClick={handleCancelEdit}
                        title="Annuleren"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="template-info">
                      <span className="template-name">{template.name}</span>
                      <span className="template-segments-count">
                        {template.segments.length} {template.segments.length === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <div className="template-actions">
                      <button 
                        className="template-view-btn" 
                        onClick={() => toggleTemplateExpand(index)}
                        title={expandedTemplate === index ? "Verbergen" : "Bekijken"}
                      >
                        <FontAwesomeIcon icon={expandedTemplate === index ? faEyeSlash : faEye} />
                      </button>
                      <button 
                        className="template-edit-btn" 
                        onClick={() => handleEditClick(index)}
                        title="Bewerken"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="template-delete-btn" 
                        onClick={() => handleDeleteClick(index)}
                        title="Verwijderen"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              {/* Expanded template content */}
              {expandedTemplate === index && (
                <div className="template-content">
                  <div className="template-content-layout">
                    <div className="template-segments-container">
                      <div className="template-segments-header">
                        <span className="segments-title">Segmenten:</span>
                        <button 
                          className="add-segment-btn"
                          onClick={() => handleAddSegment(index)}
                          title="Nieuw segment toevoegen"
                        >
                          + Toevoegen
                        </button>
                      </div>
                      <div className="template-segments-list">
                        {template.segments.map((segment, segIndex) => (
                          <div key={segIndex} className="template-segment-item">
                            <div 
                              className="segment-color"
                              style={{ backgroundColor: segment.style.backgroundColor }}
                            ></div>
                            {editSegmentIndex === segIndex && editTemplateIndex === index ? (
                              <div className="segment-edit-form">
                                <input
                                  type="text"
                                  value={editSegmentName}
                                  onChange={(e) => setEditSegmentName(e.target.value)}
                                  className="segment-edit-input"
                                  autoFocus
                                />
                                <div className="segment-edit-buttons">
                                  <button 
                                    className="segment-save-btn" 
                                    onClick={() => handleSaveSegmentEdit(index, segIndex)}
                                    title="Opslaan"
                                  >
                                    <FontAwesomeIcon icon={faSave} />
                                  </button>
                                  <button 
                                    className="segment-cancel-btn" 
                                    onClick={handleCancelSegmentEdit}
                                    title="Annuleren"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <span className="segment-name">{segment.option}</span>
                                <div className="segment-actions">
                                  <button 
                                    className="segment-edit-btn" 
                                    onClick={() => handleEditSegmentClick(index, segIndex, segment.option)}
                                    title="Bewerken"
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button 
                                    className="segment-delete-btn" 
                                    onClick={() => handleDeleteSegmentClick(index, segIndex)}
                                    title="Verwijderen"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>
          ))}
        </div>
      )}
      

    </div>
  );
};

export default Account;